import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { AlertCircle, CheckCircle, Calendar, FileCheck, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';

const BACKEND_URL = getBackendUrl();

const DuplicateChecker = () => {
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(null);
  const [checkDate, setCheckDate] = useState('');

  const runDuplicateCheck = async () => {
    setChecking(true);
    try {
      const token = storage.getItem('token');
      const endpoint = checkDate 
        ? `${BACKEND_URL}/api/admin/duplicate-check?check_date=${checkDate}`
        : `${BACKEND_URL}/api/admin/duplicate-check`;
      
      const response = await axios.post(
        endpoint,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setResults(response.data.results);
      
      const { status, summary } = response.data.results;
      
      if (status === 'clean') {
        toast.success('✅ Δεν βρέθηκαν επαναλήψεις στις τελευταίες 365 ημέρες!');
      } else if (status === 'duplicates_found') {
        toast.warning(`⚠️ Βρέθηκαν ${summary.total_duplicates_found} επαναλήψεις!`);
      } else {
        toast.info('ℹ️ Έλεγχος ολοκληρώθηκε');
      }
      
    } catch (error) {
      console.error('Duplicate check error:', error);
      toast.error(error.response?.data?.detail || 'Σφάλμα κατά τον έλεγχο επαναλήψεων');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Copy className="w-6 h-6 text-blue-600" />
            Έλεγχος Επαναλήψεων Περιεχομένου (365 Ημέρες)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Ελέγχει αν το περιεχόμενο έχει επαναληφθεί στις τελευταίες 365 ημέρες.
              Χρήσιμο για να αποφύγετε duplicate content και να διατηρήσετε το περιεχόμενο φρέσκο!
            </p>

            <div className="flex items-center gap-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={checkDate}
                  onChange={(e) => setCheckDate(e.target.value)}
                  className="px-3 py-2 border rounded-md"
                  placeholder="Επιλέξτε ημερομηνία"
                />
                <span className="text-sm text-gray-500">
                  (αφήστε κενό για σήμερα)
                </span>
              </div>

              <Button
                onClick={runDuplicateCheck}
                disabled={checking}
                className="ml-auto bg-blue-600 hover:bg-blue-700"
              >
                {checking ? (
                  <>
                    <FileCheck className="w-4 h-4 mr-2 animate-spin" />
                    Έλεγχος σε εξέλιξη...
                  </>
                ) : (
                  <>
                    <FileCheck className="w-4 h-4 mr-2" />
                    Εκτέλεση Ελέγχου
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Summary Card */}
          <Card className={`border-2 ${
            results.status === 'clean' ? 'border-green-500' :
            results.status === 'duplicates_found' ? 'border-orange-500' :
            'border-gray-500'
          }`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {results.status === 'clean' ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <span className="text-green-700">Καθαρό Περιεχόμενο!</span>
                  </>
                ) : results.status === 'duplicates_found' ? (
                  <>
                    <AlertCircle className="w-6 h-6 text-orange-500" />
                    <span className="text-orange-700">Βρέθηκαν Επαναλήψεις</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-6 h-6 text-gray-500" />
                    <span className="text-gray-700">{results.message}</span>
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-700">
                      {results.summary.total_sections_checked}
                    </div>
                    <div className="text-sm text-gray-500">Ενότητες ελέγχθηκαν</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${
                      results.summary.total_duplicates_found === 0 ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {results.summary.total_duplicates_found}
                    </div>
                    <div className="text-sm text-gray-500">Επαναλήψεις</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">
                      {results.summary.sections_with_duplicates}
                    </div>
                    <div className="text-sm text-gray-500">Ενότητες με επαναλήψεις</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">
                      365
                    </div>
                    <div className="text-sm text-gray-500">Ημέρες έλεγχος</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Duplicates List */}
          {results.duplicates && results.duplicates.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Λεπτομέρειες Επαναλήψεων ({results.duplicates.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {results.duplicates.map((dup, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200"
                    >
                      <Copy className="w-5 h-5 text-orange-500 mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-orange-500">
                            {dup.section}
                          </Badge>
                          <Badge className="bg-blue-500">
                            {dup.similarity}% match
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-700">
                          <strong>Σημερινή ημερομηνία:</strong> {dup.today_date}
                        </div>
                        <div className="text-sm text-gray-700">
                          <strong>Επαναλήφθηκε από:</strong> {dup.duplicate_date}
                          <span className="text-gray-500 ml-2">
                            (πριν {dup.days_ago} ημέρες)
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* By Section View */}
          {results.duplicates_by_section && Object.keys(results.duplicates_by_section).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Ανά Ενότητα</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(results.duplicates_by_section).map(([section, dups]) => (
                    <div
                      key={section}
                      className="p-3 border rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{section}</span>
                        <Badge className="bg-orange-500">
                          {dups.length} επαναλήψεις
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600">
                        Ημερομηνίες: {dups.map(d => d.duplicate_date).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default DuplicateChecker;
